import {FC, useRef, useState, useMemo} from "react";
import { wrapClick } from "utils";
import { useReactToPrint } from "react-to-print";
import {
  EnvelopeIcon,
  GlobeAltIcon,
  PhoneIcon, ChevronLeftIcon,
} from "@heroicons/react/20/solid";
import { Loader } from "components/layouts";
import { gql, useQuery } from "@apollo/client";
import moment from "moment";
import lodash from "lodash";
import { ChevronRightIcon } from "@heroicons/react/24/outline";
import { FactoryAcceptanceTestServiceOrderResolutionDetails } from "components/cards/factory-acceptance-test-service-order-view";

const GET_SERVICE_ORDER = gql`
  query GetFATServiceOrder($id: ID!) {
    serviceOrder: getFactoryAcceptanceTestServiceOrder(id: $id) {
      _id   
      code
      status  
      priority
      estimatedResolutionDate

      resolution {
        conclusion
        observations
        recommendations
        supportingImageUrls
        routineTestReports
        factoryAssessment {
          complianceAndCertifications
          documentation
          environmentalImpact
          equipment
          overallCondition
          productionCapacity
          safetyMeasures
          security
          utilities
          workforce
        }
        physicalInspection {
          batchNumberAndDate {
            status
            notes
          }
          classIndex {
            status
            notes
          }
          communicationPort {
            status
            notes
          }
          connectionDiagram {
            status
            notes
          }
          display {
            status
            notes
          }
          ecgPrintedOnMeter {
            status
            notes
          }
          keypad {
            status
            notes
          }
          ledIndicator {
            status
            notes
          }
          manufacturersNameAndTrademark {
            status
            notes
          }
          monthAndYearOfManufacture {
            status
            notes
          }
          numberOfPhasesAndWires {
            status
            notes
          }
          provisionForMounting {
            status
            notes
          }
          provisionForSealing {
            status
            notes
          }
          referenceVoltage {
            status
            notes
          }
          serialNumber {
            status
            notes
          }
          tariff {
            status
            notes
          }
          terminalCover {
            status
            notes
          }
          terminals {
            status
            notes
          }
          typeDesignation {
            status
            notes
          }
          userInterfaceUnit {
            status
            notes
          }
        }
        productCharacteristics {
          energy
          frequency
          modeOfPayment
          operatingCurrent
          operatingVoltage
          reactiveEnergy
          standards
          technology
        }
        productInfo {
          description
          packaging
          sampledQuantity
          samplingTechnique
          serialNumbers
          testEquipment
          type
        }
        representatives {
          designation
          fullName
          phoneNumber
        }
        test {
          metrologicalPerformance {
            reference
            status
          }
          tamperTests {
            reference
            status
          }
          functionalTests {
            reference
            status
          }
          tariffChecks {
            reference
            status
          }
          STSComplianceTest {
            reference
            status
          }
          detailedTest {
            reference
            status
          }
          eventsAndAlarms {
            reference
            status
          }
          voltageVariation {
            reference
            status
          }
          DLMSCommunication {
            reference
            status
          }
        }
        testInfo {
          address
          areaOfDeployment
          fatDate
          fatStartDate
          fatToDate
          ghanaPostAddress
          numberOfECGReps
          numberOfSupplierReps
          projectName
        }
      }
      result
      createdAt
      updatedAt 
    }
  }
`;

interface FATServiceOrderDetailsContainer {
  deliveryRequest: any;
  id: string;
  code: string;
  status: string;
  createdAt: string;
  updatedAt: string;
}

// eslint-disable-next-line @typescript-eslint/no-redeclare
const FATServiceOrderDetailsContainer: FC<
  FATServiceOrderDetailsContainer
> = ({ deliveryRequest, code }) => {

  const [selectedServiceOrder, setSelected] = useState(0);
  const numOfOrders = useMemo(() => deliveryRequest?.response?.FATServiceOrders?.length, [deliveryRequest]);

  const printRef = useRef<any>();
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: code,
    bodyClass: "w-[1000px]",
  });

  const { data, loading } = useQuery(GET_SERVICE_ORDER, {
    variables: {
      id: deliveryRequest?.response?.FATServiceOrders?.[selectedServiceOrder]?._id,
    },
    fetchPolicy: "network-only",
  });

  return (
    <div className="flex-1 flex flex-col overflow-hidden">
      <div className={"flex items-center gap-2 pt-6 pb-2 pl-6"}>
        <button
          disabled={selectedServiceOrder === 0}
          className={"bg-gray-100 hover:bg-gray-200 disabled:hover:bg-gray-100 duration-150 text-gray-400 p-1 grid place-items-center rounded border border-gray-200 disabled:cursor-not-allowed"}
          onClick={() => setSelected(selectedServiceOrder - 1)}
        >
          <ChevronLeftIcon className={"w-5"} />
        </button>
        <div>
          {selectedServiceOrder + 1} / {numOfOrders} Tests
        </div>
        <button
          disabled={selectedServiceOrder === numOfOrders - 1}
          className={"bg-gray-100 hover:bg-gray-200 disabled:hover:bg-gray-100 duration-150 text-gray-400 p-1 grid place-items-center rounded border border-gray-200 disabled:cursor-not-allowed"}
          onClick={() => setSelected(selectedServiceOrder + 1)}
        >
          <ChevronRightIcon className={"w-5"} />
        </button>
      </div>
      <div ref={printRef} className="flex-1 flex flex-col overflow-hidden">
        <div className="hidden print:flex flex-row items-center space-x-4 justify-center py-6 border-b border-gray-300">
          <div>
            <img
              alt="ECG Logo"
              src={"/logo.png"}
              className="h-32 w-32"
            />
          </div>
          <div className="space-y-1 text-gray-900">
            <h1 className="text-xl text-primary-600 font-bold">
              ELECTRICITY COMPANY OF GHANA LIMITED
            </h1>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <EnvelopeIcon className="h-4 w-4" />
                <span>P.O BOX GP 521, ACCRA</span>
              </div>
              <div className="flex items-center space-x-2">
                <PhoneIcon className="h-4 w-4" />
                <span>0302-611-611</span>
              </div>
              <div className="flex items-center space-x-2">
                <GlobeAltIcon className="h-4 w-4" />
                <span>ecg@ecggh.com</span>
              </div>
            </div>
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <span className="font-semibold">VAT REG:</span>
                <span>714V000395</span>
              </div>
              <div className="flex items-center space-x-2">
                <span className="font-semibold">Website:</span>
                <span>https://www.ecggh.com/</span>
              </div>
            </div>
          </div>
        </div>
        {loading ? (
          <Loader />
        ) : data?.serviceOrder?.status === "Completed" ? (
          <div className="p-6 overflow-y-auto flex-1">
            <div className="px-5 flex justify-between items-center cursor-pointer">
              <div>
                <h3 className="text-md leading-6 font-normal text-gray-900 dark:text-gray-100">
                  Factory Acceptance Test Order Information
                </h3>
                <p className="mt-1 text-xs text-gray-500">
                  Details about the Factory Acceptance Test
                </p>
              </div>
            </div>
            <FactoryAcceptanceTestServiceOrderResolutionDetails
              resolution={data?.serviceOrder?.resolution}
              result={data?.serviceOrder?.result}
            />
          </div>
        ) : (
          <div className="space-y-6 divide-y divide-gray-200 px-6 pb-6 overflow-y-auto flex-1">
            <div className="p-4">
              <div className="flex justify-between items-center cursor-pointer">
                <div>
                  <h3 className="text-md leading-6 font-normal text-gray-900 dark:text-gray-100">
                    Factory Acceptance Test Order Information
                  </h3>
                  <p className="mt-1 text-xs text-gray-500">
                    Details about the Factory Acceptance Test
                  </p>
                </div>
                <div />
              </div>
              <div className="mt-6">
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-md p-3 space-y-6 divide-y divide-gray-200">
                  <div className="">
                    <span className="text-xs font-light">
                      Order Information
                    </span>
                    <div className="grid grid-cols-3 gap-6 mt-2">
                      <div className="">
                        <span className="block text-sm font-light text-gray-700">
                          Code
                        </span>
                        <div className="mt-1 block w-full sm:text-sm">
                          {data?.serviceOrder?.code || "N/A"}
                        </div>
                      </div>

                      <div className={""}>
                        <span className="block text-sm font-light text-gray-700">
                          Initiated At
                        </span>
                        <div className="mt-1 block w-full sm:text-sm">
                          {data?.serviceOrder?.createdAt
                            ? moment(data?.serviceOrder?.createdAt).format(
                              "DD/MM/YYYY hh:mm A"
                            )
                            : "N/A"}
                        </div>
                      </div>
                      <div className={"col-start-1"}>
                        <span className="block text-sm font-light text-gray-700">
                          Status
                        </span>
                        <div className="mt-1 block w-full sm:text-sm">
                          {lodash.startCase(data?.serviceOrder?.status) || "N/A"}
                        </div>
                      </div>
                      <div className="">
                        <span className="block text-sm font-light text-gray-700">
                          Priority
                        </span>
                        <div className="mt-1 block w-full sm:text-sm">
                          {data?.serviceOrder?.priority || "N/A"}
                        </div>
                      </div>
                      <div>
                        <span className="block text-sm font-light text-gray-700">
                          Estimated Resolution Date
                        </span>
                        <div className="mt-1 block w-full sm:text-sm">
                          {data?.serviceOrder?.estimatedResolutionDate
                            ? moment(
                              data?.serviceOrder?.estimatedResolutionDate
                            ).format("DD/MM/YYYY")
                            : "N/A"}
                        </div>
                      </div>
                      <div>
                        <span className="block text-sm font-light text-gray-700">
                          Last Updated At
                        </span>
                        <div className="mt-1 block w-full sm:text-sm">
                          {data?.serviceOrder?.updatedAt
                            ? moment(data?.serviceOrder?.updatedAt).format(
                              "DD/MM/YYYY hh:mm A"
                            )
                            : "N/A"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-gray-50 dark:bg-gray-800 px-4 pb-3 sm:py-4 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200">
        <button
          type="button"
          onClick={wrapClick(handlePrint)}
          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
        >
          Print
        </button>
      </div>
    </div>
  );
};

export default FATServiceOrderDetailsContainer;
